import React, { useState, useEffect, useRef } from 'react';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../shared/layout/DashboardLayout';
import { MetricCard } from '../shared/layout/CardLayout';
import { TelemetryChart } from '../shared/charts/TelemetryChart';
import { AlertTrendChart } from '../shared/charts/AlertTrendChart';
import { useAlertSubscription } from '../shared/hooks/useAlertSubscription';
import { useTelemetrySubscription } from '../shared/hooks/useTelemetrySubscription';
import { useRealTimeData } from '../shared/hooks/useRealTimeData';
import { Alert, Machine, TelemetrySample } from '@cbm/shared';

interface OEEMetrics {
  availability: number;
  performance: number;
  quality: number;
  overall: number;
  microStopsPerHour: number;
  cycleTimeStability: number;
}

export function PlantManagerDashboard() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [oeeMetrics, setOeeMetrics] = useState<Record<string, OEEMetrics>>({});
  const [loading, setLoading] = useState(true);
  const subscribedRef = useRef<Set<string>>(new Set());

  const { alerts } = useAlertSubscription();
  const { samples, subscribe } = useTelemetrySubscription();
  const { isConnected } = useRealTimeData(null as any, () => true as any, () => true as any, () => true as any);

  useEffect(() => {
    console.log('PlantManager: Alerts updated', { count: alerts.length, alerts });
  }, [alerts]);

  useEffect(() => {
    if (samples.length === 0) {
      setLoading(false);
      return;
    }

    const streamingMachineIds = [...new Set(samples.map(s => s.machineId))];
    const machineDisplayNames: Record<string, string> = {
      'M-001': 'Production Line A - Station 1',
      'M-002': 'Production Line A - Station 2',
      'M-003': 'Production Line B - Station 1',
    };
    const dynamicMachines: Machine[] = streamingMachineIds.map(machineId => ({
      pk: 'TENANT#demo',
      sk: `MACHINE#${machineId}`,
      displayName: machineDisplayNames[machineId] || machineId,
      designVariant: machineId === 'machine-003' ? 'Model Y-150' : 'Model X-200',
      usageProfile: 'normal',
      createdAt: new Date().toISOString(),
    }));

    setMachines(dynamicMachines);
    setLoading(false);
  }, [samples]);

  useEffect(() => {
    if (!isConnected) {
      subscribedRef.current.clear();
      return;
    }
    if (machines.length === 0) return;

    machines.forEach(machine => {
      const machineId = machine.sk.split('#')[1];
      if (subscribedRef.current.has(machineId)) return;

      subscribedRef.current.add(machineId);
      console.log('PlantManager: Subscribing to', machineId);
      subscribe(machineId);
    });
  }, [isConnected, machines, subscribe]);

  useEffect(() => {
    if (samples.length === 0) {
      console.log('PlantManager: No samples available yet');
      return;
    }

    // DIAGNOSTIC: Show which machines are actually streaming
    const streamingMachines = new Set(samples.map(s => s.machineId));
    console.log('PlantManager: Distinct streaming machines:', [...streamingMachines]);
    console.log('PlantManager: Total samples:', samples.length);

    console.log('PlantManager: Calculating OEE metrics', { 
      totalSamples: samples.length,
      machines: machines.map(m => m.sk.split('#')[1])
    });

    const metrics: Record<string, OEEMetrics> = {};
    
    machines.forEach(machine => {
      const machineId = machine.sk.split('#')[1];
      const machineSamples = samples.filter(s => s.machineId === machineId).slice(-20);
      
      console.log('PlantManager: Machine samples', { 
        machineId, 
        sampleCount: machineSamples.length,
        latestSample: machineSamples[machineSamples.length - 1]?.timestamp
      });
      
      if (machineSamples.length === 0) {
        console.log('PlantManager: No samples for', machineId, '- showing zeros');
        metrics[machineId] = {
          availability: 0,
          performance: 0,
          quality: 0,
          overall: 0,
          microStopsPerHour: 0,
          cycleTimeStability: 0
        };
        return;
      }

      const getAvg = (tag: string) => {
        const values = machineSamples.map(s => s.tags.find(t => t.name === tag)?.value || 0);
        return values.reduce((a, b) => a + b, 0) / values.length;
      };

      const throughput = getAvg('throughput');
      const cycleTime = getAvg('cycle_time');
      const temp = getAvg('temperature');
      const vibration = getAvg('vibration_level');
      
      const availability = Math.max(0, Math.min(100, 100 - (vibration * 5)));
      const performance = Math.max(0, Math.min(100, (throughput / 100) * 100));
      const quality = Math.max(0, Math.min(100, 100 - ((temp - 40) / 2)));
      const overall = (availability * performance * quality) / 10000;
      const microStopsPerHour = Math.max(0, vibration * 2);
      const cycleTimeStability = Math.max(0, Math.min(100, 100 - (cycleTime - 30)));

      metrics[machineId] = {
        availability,
        performance,
        quality,
        overall,
        microStopsPerHour,
        cycleTimeStability
      };
    });

    setOeeMetrics(metrics);
  }, [samples, machines]);

  const openAlerts = alerts.filter(a => a.status === 'OPEN').length;
  const ackAlerts = alerts.filter(a => a.status === 'ACKNOWLEDGED').length;
  const agingAlerts = alerts.filter(a => {
    const hours = (Date.now() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60);
    return hours > 4 && a.status === 'OPEN';
  }).length;

  const avgOEE = Object.values(oeeMetrics).reduce((sum, m) => sum + m.overall, 0) / (Object.keys(oeeMetrics).length || 1);

  if (loading) {
    return (
      <DashboardContentShell>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardContentShell>
    );
  }

  return (
    <DashboardContentShell>
      {/* KPI Bar */}
      <KPIBar
        metrics={[
          { label: 'Avg OEE', value: avgOEE.toFixed(1), unit: '%', status: avgOEE >= 75 ? 'good' : avgOEE >= 60 ? 'warning' : 'critical' },
          { label: 'Open Alerts', value: openAlerts, status: openAlerts > 10 ? 'critical' : openAlerts > 5 ? 'warning' : 'good' },
          { label: 'Acknowledged', value: ackAlerts, status: 'good' },
          { label: 'Aging (>4h)', value: agingAlerts, status: agingAlerts > 5 ? 'critical' : agingAlerts > 2 ? 'warning' : 'good' },
          { label: 'Machines', value: machines.length, status: 'good' },
          { label: 'Status', value: isConnected ? 'LIVE' : 'OFFLINE', status: isConnected ? 'good' : 'critical' },
        ]}
      />

      {/* OEE Metrics Grid */}
      <DashboardSection title="OEE Performance" subtitle="Overall Equipment Effectiveness by production line">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {machines.map(machine => {
            const machineId = machine.sk.split('#')[1];
            const metrics = oeeMetrics[machineId];
            const machineSamples = samples.filter(s => s.machineId === machineId);
            const hasData = machineSamples.length > 0;
            const lastSample = machineSamples[machineSamples.length - 1];
            
            if (!metrics) return null;

            return (
              <div key={machine.sk} className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">{machine.displayName}</h3>
                  {hasData ? (
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                      metrics.overall >= 85 ? 'bg-emerald-500/20 text-emerald-400' :
                      metrics.overall >= 70 ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {metrics.overall.toFixed(1)}%
                    </div>
                  ) : (
                    <div className="px-3 py-1 rounded-full text-xs font-bold bg-slate-700 text-slate-400">
                      NO DATA
                    </div>
                  )}
                </div>

                {!hasData && (
                  <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                    <div className="text-sm text-amber-400 font-semibold mb-1">⚠️ No live telemetry</div>
                    <div className="text-xs text-slate-400">
                      Start simulation for {machineId} or enable Fleet mode
                    </div>
                  </div>
                )}

                {hasData && (
                  <div className="mb-3 text-xs text-slate-500">
                    Last update: {new Date(lastSample.timestamp).toLocaleTimeString()} • {machineSamples.length} samples
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-800 border border-slate-700 rounded p-3">
                    <div className="text-xs text-slate-400 uppercase mb-1">Availability</div>
                    <div className="text-2xl font-bold text-blue-400">{metrics.availability.toFixed(1)}%</div>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded p-3">
                    <div className="text-xs text-slate-400 uppercase mb-1">Performance</div>
                    <div className="text-2xl font-bold text-purple-400">{metrics.performance.toFixed(1)}%</div>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded p-3">
                    <div className="text-xs text-slate-400 uppercase mb-1">Quality</div>
                    <div className="text-2xl font-bold text-emerald-400">{metrics.quality.toFixed(1)}%</div>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded p-3">
                    <div className="text-xs text-slate-400 uppercase mb-1">Stability</div>
                    <div className="text-2xl font-bold text-cyan-400">{metrics.cycleTimeStability.toFixed(1)}%</div>
                  </div>
                </div>

                <div className="flex justify-between items-center p-3 bg-slate-800 border border-slate-700 rounded">
                  <span className="text-sm text-slate-300">Micro-stops/hr</span>
                  <span className={`text-lg font-bold ${
                    metrics.microStopsPerHour > 10 ? 'text-red-400' :
                    metrics.microStopsPerHour > 5 ? 'text-amber-400' :
                    'text-emerald-400'
                  }`}>
                    {metrics.microStopsPerHour.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </DashboardSection>

      {/* Alert Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection title="Alert Distribution" subtitle="Current alert status breakdown">
          <div className="grid grid-cols-2 gap-3">
            <MetricCard title="Total Alerts" value={alerts.length} />
            <MetricCard title="Open" value={openAlerts} />
            <MetricCard title="Acknowledged" value={ackAlerts} />
            <MetricCard title="Aging (>4h)" value={agingAlerts} />
          </div>
        </DashboardSection>

        <DashboardSection title="Alert Aging" subtitle="Time since alert creation">
          <div className="space-y-2">
            {[
              { label: '< 1 hour', count: alerts.filter(a => (Date.now() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60) < 1).length, color: 'emerald' },
              { label: '1-4 hours', count: alerts.filter(a => { const h = (Date.now() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60); return h >= 1 && h < 4; }).length, color: 'amber' },
              { label: '4-24 hours', count: alerts.filter(a => { const h = (Date.now() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60); return h >= 4 && h < 24; }).length, color: 'orange' },
              { label: '> 24 hours', count: alerts.filter(a => (Date.now() - new Date(a.createdAt).getTime()) / (1000 * 60 * 60) >= 24).length, color: 'red' },
            ].map((item, idx) => (
              <div key={idx} className={`flex justify-between items-center p-3 rounded bg-${item.color}-500/10 border border-${item.color}-500/30`}>
                <span className="text-sm text-slate-300">{item.label}</span>
                <span className={`text-lg font-bold text-${item.color}-400 bg-${item.color}-500/20 px-3 py-1 rounded-full`}>
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>

      {/* Alert Trend Chart */}
      <DashboardWidget title="Alert Trends" subtitle="24-hour alert activity">
        <AlertTrendChart alerts={alerts} height={300} timeRange="day" />
      </DashboardWidget>
    </DashboardContentShell>
  );
}
